import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Download, ArrowUpRight } from "lucide-react";
import { portalGet, portalLookup, portalPay, portalVerifyPayment, portalContractPdfUrl, extractApiError } from "@/lib/api";
import { SigningForm } from "@/components/booking/SigningForm";
import { ContractSections, SummaryTable } from "@/components/booking/ContractBody";
import { SupplyPortalPanel, DesignFiles } from "@/components/booking/SupplyPortalPanel";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const RequestSummary = ({ sr }) => (
  <div className="p-6 border text-sm" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-request-summary">
    <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-rust)" }}>Your Social Supply request (under review)</div>
    {sr.estimate.lines.map((l) => (
      <div key={l.key} className="flex justify-between gap-3 py-0.5"><span>{l.label}{l.quantity > 1 && !l.key.includes(":") ? ` × ${l.quantity}` : ""}</span><span>{l.amount == null ? l.note : money(l.amount)}</span></div>
    ))}
    {sr.estimate.fulfillment && <div className="flex justify-between gap-3 py-0.5"><span>{sr.estimate.fulfillment.label}</span><span>{sr.estimate.fulfillment.amount == null ? sr.estimate.fulfillment.note : sr.estimate.fulfillment.amount ? money(sr.estimate.fulfillment.amount) : (sr.estimate.fulfillment.note || "Free")}</span></div>}
    <div className="flex justify-between font-semibold pt-2 mt-2 border-t" style={{ borderColor: "var(--ss-border)" }}><span>Estimated total</span><span>{money(sr.estimate.estimated_total)}</span></div>
    {sr.needed_by && <div className="mt-2" data-testid="portal-needed-by">Needed by: {new Date(`${sr.needed_by}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>}
    <p className="text-xs mt-2" style={{ color: "var(--ss-muted)" }}>This is an estimate, not a confirmed order. We'll review your request and send your approved price and agreement here.</p>
  </div>
);

const STEPS = [
  ["enquiry", "Request Received"], ["approved", "Approved"], ["contract_signed", "Contract Signed"],
  ["confirmed", "Retainer Paid · Confirmed"], ["paid_in_full", "Paid in Full"], ["completed", "Completed"],
];
const STATUS_TEXT = {
  enquiry: "Pending review — we're reviewing your request.", tentative: "Pending review — we're reviewing your request.",
  quoted: "Pending review — we're reviewing your request.",
  approved: "Approved — please review and sign your agreement.",
  contract_signed: "Contract signed — pay your booking retainer to reserve your date.",
  deposit: "Retainer received.", confirmed: "Confirmed — your date is reserved.",
  paid_in_full: "Paid in full — see you soon!", completed: "Completed — thank you for celebrating with us.",
  declined: "This booking request was declined.", cancelled: "This booking has been cancelled.",
};

const SUPPLY_ONLY_STEPS = [["enquiry", "Request Received"], ["approved", "Approved"], ["contract_signed", "Agreement Signed"], ["paid_in_full", "Paid in Full · Confirmed"], ["completed", "Completed"]];
const EXEC_STEPS = [["enquiry", "Request Received"], ["approved", "Approved"], ["contract_signed", "Agreement Signed"], ["confirmed", "Paid in Full · Confirmed"], ["completed", "Completed"]];

const StepTracker = ({ status, steps = STEPS }) => {
  const order = steps.map((s) => s[0]);
  const idx = Math.max(order.indexOf(status === "deposit" ? "contract_signed" : ["tentative", "quoted"].includes(status) ? "enquiry" : status), 0);
  return (
    <ol className={`grid grid-cols-2 gap-3 ${steps.length === 6 ? "md:grid-cols-6" : "md:grid-cols-5"}`} data-testid="portal-steps">
      {steps.map(([k, label], i) => (
        <li key={k} className="flex items-center gap-2 text-xs" style={{ color: i <= idx ? "var(--ss-cream)" : "var(--ss-muted)" }}>
          <span className="h-6 w-6 shrink-0 flex items-center justify-center border" style={{ borderColor: i <= idx ? "var(--ss-rust)" : "var(--ss-border)", background: i <= idx ? "var(--ss-rust)" : "transparent", color: "#F5F1E8" }}>
            {i <= idx ? <Check size={12} /> : i + 1}
          </span>
          {label}
        </li>
      ))}
    </ol>
  );
};

const Lookup = () => {
  const [f, setF] = useState({ booking_ref: "", email: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await portalLookup(f); setSent(true); } catch (err) { toast.error(extractApiError(err)); }
  };
  return (
    <div className="pt-40 md:pt-52 pb-32 mx-auto max-w-[900px] px-5 md:px-10" data-testid="portal-lookup">
      <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>My Booking</div>
      <h1 className="mt-4 font-display font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">Find your booking.</h1>
      {sent ? (
        <p className="mt-8 font-body text-lg" data-testid="portal-lookup-sent" style={{ color: "var(--ss-cream-dim)" }}>
          If that Booking ID and email match, we've emailed you a secure link to your booking portal.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-8">
          <input required className="editorial-input" placeholder="Booking ID (SS-2026-0001)" data-testid="portal-lookup-ref"
                 value={f.booking_ref} onChange={(e) => setF({ ...f, booking_ref: e.target.value })} />
          <input required type="email" className="editorial-input" placeholder="Email used to book" data-testid="portal-lookup-email"
                 value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <button className="btn-primary md:col-span-2 justify-self-start" data-testid="portal-lookup-submit">Email my link <ArrowUpRight className="arrow" size={14} /></button>
        </form>
      )}
    </div>
  );
};

const QuoteCard = ({ q }) => (
  <div className="p-6 border" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-quote">
    <div className="font-label text-[0.6rem] mb-4" style={{ color: "var(--ss-rust)" }}>Your Approved Quote</div>
    {q.has_service && q.executive && (
      <div data-testid="portal-quote-executive" className="text-sm">
        <div className="font-label text-[0.55rem] mb-1" style={{ color: "var(--ss-muted)" }}>Executive Graduation Experience · 100% due at signing</div>
        <div>No booking retainer or remaining balance — the full package price is paid at signing.</div>
      </div>
    )}
    {q.has_service && !q.executive && (
      <div data-testid="portal-quote-service">
        <div className="font-label text-[0.55rem] mb-1" style={{ color: "var(--ss-muted)" }}>Service · retainer + balance</div>
        {q.line_items.map((li, i) => (
          <div key={i} className="flex justify-between text-sm py-1"><span>{li.label}</span><span>{money(li.amount)}</span></div>
        ))}
        <div className="flex justify-between text-sm"><span>Booking Retainer (non-refundable)</span><span data-testid="portal-retainer">{money(q.retainer)}</span></div>
        <div className="flex justify-between text-sm"><span>Remaining service balance</span><span>{money(q.balance)}</span></div>
        <div className="text-xs pt-1" style={{ color: "var(--ss-muted)" }}>Balance due by {q.balance_due_display}</div>
      </div>
    )}
    {q.has_supply && (
      <div className={q.has_service ? "mt-4 pt-4 border-t" : ""} style={{ borderColor: "var(--ss-border)" }} data-testid="portal-quote-supply">
        <div className="font-label text-[0.55rem] mb-1" style={{ color: "var(--ss-muted)" }}>Social Supply custom items · 100% due at purchase</div>
        {q.supply_items.map((it, i) => (
          <div key={i} className="flex justify-between gap-3 text-sm py-1">
            <span>{it.product}{it.quantity > 1 ? ` × ${it.quantity}` : ""}{it.customization && <span style={{ color: "var(--ss-muted)" }}> — {it.customization}</span>}</span>
            <span>{money(it.quantity * it.unit_price)}</span>
          </div>
        ))}
        {q.fulfillment === "delivery" && <div className="flex justify-between text-sm"><span>Local delivery</span><span>{money(q.delivery_charge)}</span></div>}
        {q.fulfillment === "shipping" && <div className="flex justify-between text-sm"><span>Shipping</span><span data-testid="portal-shipping-charge">{money(q.shipping_charge)}</span></div>}
        {q.fulfillment === "pickup" && <div className="flex justify-between text-sm"><span>Pickup ({q.pickup_location === "stl" ? "St. Louis" : "Chicago"})</span><span>Free</span></div>}
        <div className="flex justify-between text-sm"><span>Social Supply total</span><span data-testid="portal-supply-total">{money(q.supply_total)}</span></div>
      </div>
    )}
    {q.executive ? (
      <div className="mt-3 pt-3 border-t space-y-1 text-sm" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-exec-summary">
        <div className="flex justify-between font-semibold"><span>Approved Total</span><span data-testid="portal-total">{money(q.total)}</span></div>
        {q.amount_paid > 0
          ? <div className="flex justify-between"><span>Paid in full</span><span data-testid="portal-paid">{money(q.amount_paid)}</span></div>
          : <div className="flex justify-between"><span>Amount due now (100%)</span><span data-testid="portal-first-payment">{money(q.first_payment)}</span></div>}
        <p className="text-xs" style={{ color: "var(--ss-muted)" }}>No remaining balance after payment.</p>
      </div>
    ) : (
    <div className="mt-3 pt-3 border-t space-y-1 text-sm" style={{ borderColor: "var(--ss-border)" }}>
      <div className="flex justify-between font-semibold"><span>Approved Total</span><span data-testid="portal-total">{money(q.total)}</span></div>
      <div className="flex justify-between"><span>Due at signing</span><span data-testid="portal-first-payment">{money(q.first_payment)}</span></div>
      <div className="flex justify-between"><span>Paid so far</span><span data-testid="portal-paid">{money(q.amount_paid)}</span></div>
      <div className="flex justify-between"><span>Remaining</span><span data-testid="portal-remaining">{money(q.amount_remaining)}</span></div>
    </div>
    )}
    {q.note && <p className="mt-4 text-sm whitespace-pre-wrap" style={{ color: "var(--ss-cream-dim)" }}>{q.note}</p>}
  </div>
);

const SignedContract = ({ token, signed }) => (
  <details className="p-6 border" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-signed-contract">
    <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-3">
      <span className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Signed Agreement · {new Date(signed.signed_at).toLocaleString()}</span>
      <a href={portalContractPdfUrl(token)} className="btn-outline inline-flex items-center gap-2" data-testid="portal-download-pdf" onClick={(e) => e.stopPropagation()}>
        <Download size={14} /> Download PDF
      </a>
    </summary>
    <div className="mt-6 space-y-8">
      <SummaryTable rows={signed.contract.summary} testId="signed-summary" />
      <ContractSections sections={signed.contract.sections} />
      <div>
        <div className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-rust)" }}>Electronic Signature</div>
        {signed.signature_mode === "draw"
          ? <img src={signed.signature_data} alt="Signature" className="h-20 bg-white border p-1" />
          : <div className="text-3xl italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{signed.signature_data}</div>}
        <div className="mt-2 text-xs" style={{ color: "var(--ss-muted)" }}>Signed by {signed.signer_name} · Fingerprint {signed.sha256.slice(0, 16)}…</div>
      </div>
    </div>
  </details>
);

export default function MyBooking() {
  const { token } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    try { setData(await portalGet(token)); } catch (e) { setErr(extractApiError(e, "Booking not found")); }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const sid = sp.get("session_id");
    if (sid) {
      portalVerifyPayment(token, sid)
        .then((r) => { if (r.payment_status === "paid") toast.success("Payment received — thank you!"); })
        .catch(() => {})
        .finally(() => { setSp({}, { replace: true }); load(); });
    } else {
      if (sp.get("payment") === "cancelled") toast("Payment cancelled — you can try again any time.");
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pay = async (kind) => {
    setPaying(true);
    try { const r = await portalPay(token, kind); window.location.href = r.url; }
    catch (e) { toast.error(extractApiError(e)); setPaying(false); }
  };

  if (!token) return <Lookup />;
  if (err) return <div className="pt-40 pb-32 px-5 text-center" data-testid="portal-error">{err}</div>;
  if (!data) return <div className="pt-40 pb-32 px-5 text-center" data-testid="portal-loading">Loading your booking…</div>;
  const b = data.booking;
  const supplyOnly = (b.services || []).every((s) => s === "supply");
  const statusText = b.status === "contract_signed" && data.quote?.executive ? "Agreement signed — pay your Executive package in full to reserve your date."
    : supplyOnly && b.status === "contract_signed" ? "Agreement signed — pay your order in full to confirm it." : STATUS_TEXT[b.status] || "";

  return (
    <div className="pt-40 md:pt-52 pb-32 mx-auto max-w-[1100px] px-5 md:px-10 space-y-12" data-testid="portal-page">
      <div>
        <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }} data-testid="portal-booking-ref">{supplyOnly ? "Order Request" : "Booking"} {b.booking_ref}</div>
        <h1 className="mt-4 font-display font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">Hi {b.name?.split(" ")[0]}.</h1>
        <p className="mt-5 font-body text-base md:text-lg" style={{ color: "var(--ss-cream-dim)" }} data-testid="portal-status-text">{statusText}</p>
        {data.decline_reason && <p className="mt-2 text-sm" style={{ color: "var(--ss-cream-dim)" }}>{data.decline_reason}</p>}
      </div>
      {!["declined", "cancelled"].includes(b.status) && <StepTracker status={b.status} steps={(b.services || []).every((s) => s === "supply") ? SUPPLY_ONLY_STEPS : data.quote?.executive ? EXEC_STEPS : STEPS} />}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 border text-sm space-y-1" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-event">
          <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-rust)" }}>{supplyOnly && !data.quote?.executive && !data.supply_request?.event_address ? "Your Order" : "Your Event"}</div>
          <div>{supplyOnly && !data.supply_request?.event_address ? "Needed by: " : ""}{new Date(`${b.event_date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
          {b.event_location && <div>{b.event_location} · {b.event_type} · {b.guest_count} guests</div>}
          {data.supply_request?.event_address && <div data-testid="portal-event-address">Event address: {data.supply_request.event_address}</div>}
        </div>
        {data.quote && <QuoteCard q={data.quote} />}
      </div>

      {data.can_pay_retainer && (
        <div className="p-6 border" style={{ borderColor: "var(--ss-rust)" }}>
          <p className="mb-4 text-sm">
            {data.quote.has_service
              ? "Your agreement is signed. Your date is reserved once this payment is successfully completed."
              : "Your agreement is signed. Your order is confirmed once it is paid in full."}
            {data.quote.has_service && data.quote.has_supply && !data.quote.executive && ` Includes your booking retainer (${money(data.quote.retainer)}) and your Social Supply order in full (${money(data.quote.supply_total)}).`}
          </p>
          <button className="btn-primary" disabled={paying} onClick={() => pay("retainer")} data-testid="portal-pay-retainer">
            {paying ? "Opening Stripe…" : data.quote.has_service
              ? (data.quote.executive ? `Pay Executive Package in Full · ${money(data.quote.first_payment)}` : data.quote.has_supply ? `Pay Retainer + Social Supply · ${money(data.quote.first_payment)}` : `Pay Booking Retainer · ${money(data.quote.retainer)}`)
              : `Pay Order in Full · ${money(data.quote.first_payment)}`}
          </button>
        </div>
      )}
      {data.can_pay_balance && (
        <div className="p-6 border" style={{ borderColor: "var(--ss-gold)" }}>
          <p className="mb-4 text-sm">Remaining balance due by {data.quote.balance_due_display}.</p>
          <button className="btn-gold" disabled={paying} onClick={() => pay("balance")} data-testid="portal-pay-balance">
            {paying ? "Opening Stripe…" : `Pay Remaining Balance · ${money(data.quote.amount_remaining)}`}
          </button>
        </div>
      )}

      {data.supply_status && <SupplyPortalPanel token={token} data={data} onDone={load} onPayRevision={() => pay("revision")} paying={paying} />}
      {!data.supply_status && data.can_upload_design_files && <div className="p-6 border" style={{ borderColor: "var(--ss-border)" }}><DesignFiles token={token} data={data} onDone={load} /></div>}
      {!data.quote && data.supply_request?.estimate && <RequestSummary sr={data.supply_request} />}
      {data.can_sign && data.contract_preview && <SigningForm token={token} data={data} onDone={() => { load(); window.scrollTo(0, 0); }} />}
      {data.signed && <SignedContract token={token} signed={data.signed} />}

      {data.payments.length > 0 && (
        <div className="p-6 border" style={{ borderColor: "var(--ss-border)" }} data-testid="portal-payments">
          <div className="font-label text-[0.6rem] mb-3" style={{ color: "var(--ss-rust)" }}>Payments</div>
          {data.payments.map((p, i) => (
            <div key={i} className="flex justify-between text-sm py-1">
              <span>{{ retainer: data.quote?.executive ? "Executive Package (paid in full)" : p.supply_amount > 0 ? "Booking Retainer + Social Supply" : "Booking Retainer", supply: "Social Supply Order (paid in full)", balance: "Remaining Balance", revision: "Additional Revision Round" }[p.kind]}{p.duplicate ? " (duplicate — being reviewed for refund)" : ""} · {new Date(p.paid_at).toLocaleDateString()}</span>
              <span>{money(p.amount)}</span>
            </div>
          ))}
          {data.refunds.map((r, i) => (
            <div key={`r${i}`} className="flex justify-between text-sm py-1" style={{ color: "var(--ss-muted)" }}>
              <span>Refund · {new Date(r.at).toLocaleDateString()}</span><span>−{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
